import { useQuery } from '@tanstack/react-query'
import { API_CONFIG } from 'config/api'
import { SWAP_QUOTE_REFETCH_POLICY } from 'config/RefetchPolicies'
import { kxRouterQuoteKeyBase } from 'hooks/backend/routing'
import { useConnection } from 'hooks/useConnection'
import { SubgraphIsland } from 'hooks/useIsland'
import qs from 'qs'
import { useCallback, useMemo } from 'react'
import { QuoteResult } from 'state/routing/trades'
import { zeroAddress } from 'viem'

interface KXRouterZapResponse {
  isLoading: boolean
  isError: boolean
  data: QuoteResult | undefined
  refetch: () => Promise<void>
}

/**
 * Hook to get a quote for zapping from a single token directly into island tokens using KX Router
 * @param island - The island to zap into
 * @param tokenIn - The input token address
 * @param amount - The input amount (as native bigint)
 * @param slippage - The slippage tolerance as a percentage string (e.g., "0.5" for 0.5%)
 * @param isActive - Whether to actively fetch the quote
 * @param tokenOutAddress
 * @returns Quote data from KX Router
 */
const useKXRouterZap = (
  island: SubgraphIsland | null,
  tokenIn: string | undefined,
  amount: bigint,
  slippage: string,
  isActive: boolean,
  tokenOutAddress?: string
): KXRouterZapResponse => {
  const { chainId, account } = useConnection()

  // Prepare query parameters
  const queryParams = useMemo(() => {
    if (!tokenOutAddress || !tokenIn || !chainId || amount === 0n) {
      return null
    }

    const queryParams: any = {
      tokenInAddress: tokenIn === 'native' ? zeroAddress : tokenIn,
      tokenInChainId: chainId,
      tokenOutAddress, // Can be island token, bault token, or charm vault token
      tokenOutChainId: chainId,
      amount: amount.toString(),
      type: 'exactIn',
      slippageTolerance: parseFloat(slippage),
      refCode: 1,
      providers: 'enso',
    }

    if (account) {
      // add correct recipient address if connected
      queryParams.recipient = account
    }

    return queryParams
  }, [tokenOutAddress, tokenIn, chainId, amount, account, slippage])

  // Fetch quote from KX Router using React Query
  const { data, isLoading, isError, refetch } = useQuery<QuoteResult>({
    queryKey: [...kxRouterQuoteKeyBase, tokenIn, tokenOutAddress, amount.toString(), slippage, chainId, account],
    queryFn: async ({ signal }) => {
      if (!queryParams) {
        throw new Error('Missing query parameters')
      }

      const query = qs.stringify(queryParams)
      const response = await fetch(`${API_CONFIG.baseUrl}/quote?${query}`, { signal })

      if (!response.ok) {
        throw new Error('Failed to fetch quote')
      }

      return await response.json()
    },
    ...SWAP_QUOTE_REFETCH_POLICY,
    enabled: isActive && !!queryParams,
  })

  const refetchQuote = useCallback(async () => {
    await refetch()
  }, [refetch])

  return {
    isLoading,
    isError,
    data,
    refetch: refetchQuote,
  }
}

export default useKXRouterZap
